// Right-side drawer for a single Security Hotspot (T341) — shows the
// CWE/vulnerability context (rule lookup, same catalog as IssueDetailDrawer)
// plus the review-status transition actions the backend already exposes at
// `POST /api/v1/hotspots/{issueID}/transition`.
//
// This is a separate, lighter component from IssueDetailDrawer rather than
// an extension of it: hotspots don't need the dataflow/SCA/suggest-fix/Jira
// panels that make up most of that drawer's bulk, and they need a "Review"
// action section that ordinary issues don't have. Both share the same
// native <dialog> side-panel pattern for consistency (see IssueDetailDrawer
// for the rationale).

import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  Check,
  CheckCircle2,
  Eye,
  Loader2,
  ShieldCheck,
  ShieldQuestion,
  Sparkles,
  X,
} from 'lucide-react';
import {
  ApiError,
  HOTSPOT_REVIEW_STATUSES,
  HotspotReviewStatus,
  Issue,
  RULES_CATALOG_URL,
  Rule,
  RuleListResponse,
  fetcher,
  hotspotsApi,
} from '../lib/api';

// ---------------------------------------------------------------------------
// Tone / label tables
// ---------------------------------------------------------------------------

const STATUS_TONE: Record<HotspotReviewStatus, string> = {
  to_review: 'bg-warning/10 text-warning border-warning/40',
  acknowledged: 'bg-accent/10 text-accent border-accent/30',
  fixed: 'bg-accent-muted text-accent border-accent/30',
  safe: 'bg-bg-inset text-fg-muted border-border',
};

const STATUS_LABEL: Record<HotspotReviewStatus, string> = {
  to_review: 'To review',
  acknowledged: 'Acknowledged',
  fixed: 'Fixed',
  safe: 'Safe',
};

function asHotspotStatus(raw: string | undefined): HotspotReviewStatus {
  const found = HOTSPOT_REVIEW_STATUSES.find((s) => s === raw);
  return found ?? 'to_review';
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface HotspotDetailDrawerProps {
  /** Selected hotspot, or null when no hotspot is selected. Controlled —
   *  the parent owns selection state. */
  readonly hotspot: Issue | null;
  /** Hotspot ID the URL is asking for, used only when `hotspot` is null. */
  readonly requestedID?: string;
  readonly onClose: () => void;
  /** Called after a transition succeeds so the parent can revalidate its list. */
  readonly onTransitioned: () => void;
}

export function HotspotDetailDrawer({
  hotspot,
  requestedID,
  onClose,
  onTransitioned,
}: HotspotDetailDrawerProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const shouldOpen = hotspot !== null || (requestedID !== undefined && requestedID !== '');

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (shouldOpen && !d.open) d.showModal();
    if (!shouldOpen && d.open) d.close();
  }, [shouldOpen]);

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      aria-label="Hotspot detail"
      className="
        bg-transparent p-0 m-0
        max-w-none max-h-none w-full h-full
        backdrop:bg-bg-base/60 backdrop:backdrop-blur-sm
      "
    >
      <div className="h-full w-full flex">
        <button
          type="button"
          onClick={onClose}
          aria-hidden="true"
          tabIndex={-1}
          className="flex-1 h-full cursor-default bg-transparent border-none"
        />
        <div className="h-full w-full max-w-xl bg-bg-elevated border-l border-border shadow-2xl overflow-y-auto">
          {hotspot ? (
            <DrawerBody
              key={hotspot.id}
              hotspot={hotspot}
              onClose={onClose}
              onTransitioned={onTransitioned}
            />
          ) : (
            <DrawerMissing requestedID={requestedID} onClose={onClose} />
          )}
        </div>
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

function DrawerBody({
  hotspot,
  onClose,
  onTransitioned,
}: {
  readonly hotspot: Issue;
  readonly onClose: () => void;
  readonly onTransitioned: () => void;
}) {
  const status = asHotspotStatus(hotspot.hotspot_review_status);

  const catalog = useSWR<RuleListResponse>(
    RULES_CATALOG_URL,
    fetcher,
    { revalidateOnFocus: false },
  );
  const rule = useMemo(
    () => catalog.data?.items.find((r) => r.id === hotspot.rule_id),
    [catalog.data, hotspot.rule_id],
  );

  const fileRange = hotspot.file_path
    ? `${hotspot.file_path}:${hotspot.line_start || 1}${
        hotspot.line_end && hotspot.line_end !== hotspot.line_start ? `–${hotspot.line_end}` : ''
      }`
    : '—';

  return (
    <div className="flex flex-col h-full">
      <Header status={status} isNew={hotspot.is_new} onClose={onClose} />

      <div className="px-6 py-5 space-y-6 flex-1">
        <div>
          <p className="orb-eyebrow mb-1">security hotspot</p>
          <h2 className="font-display text-lg font-semibold text-fg leading-snug">
            {hotspot.message || <span className="text-fg-subtle">(no message)</span>}
          </h2>
        </div>

        <MetaGrid hotspot={hotspot} rule={rule} fileRange={fileRange} />

        <VulnerabilityContext hotspot={hotspot} rule={rule} catalogLoading={catalog.isLoading} />

        <ReviewPanel hotspot={hotspot} status={status} onTransitioned={onTransitioned} />
      </div>
    </div>
  );
}

function DrawerMissing({
  requestedID,
  onClose,
}: {
  readonly requestedID?: string;
  readonly onClose: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      <header className="sticky top-0 z-10 bg-bg-elevated/95 border-b border-border px-6 py-4 flex items-center gap-3">
        <span className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close hotspot detail"
          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-fg-muted hover:text-fg hover:bg-bg-hover border border-transparent hover:border-border transition-colors"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>
      <div className="px-6 py-8 text-center text-sm text-fg-muted">
        {requestedID ? (
          <p>
            Hotspot <span className="orb-code">{requestedID}</span> isn&rsquo;t in the
            current view. Loosen a filter or scroll further to find it.
          </p>
        ) : (
          <p>No hotspot selected.</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header({
  status,
  isNew,
  onClose,
}: {
  readonly status: HotspotReviewStatus;
  readonly isNew: boolean;
  readonly onClose: () => void;
}) {
  return (
    <header
      className="
        sticky top-0 z-10 bg-bg-elevated/95 backdrop-blur-sm
        border-b border-border px-6 py-4
        flex items-center gap-3
      "
    >
      <span className={`orb-badge border shrink-0 ${STATUS_TONE[status]}`}>
        <Eye className="h-3 w-3" aria-hidden="true" />
        <span>{STATUS_LABEL[status]}</span>
      </span>
      {isNew ? (
        <span
          className="orb-badge border bg-accent/15 text-accent border-accent/30 shrink-0"
          title="Introduced in the current branch"
        >
          <Sparkles className="h-3 w-3" aria-hidden="true" />
          <span>new</span>
        </span>
      ) : null}
      <span className="flex-1" />
      <button
        type="button"
        onClick={onClose}
        aria-label="Close hotspot detail"
        className="
          inline-flex items-center justify-center h-8 w-8 rounded-md
          text-fg-muted hover:text-fg hover:bg-bg-hover
          border border-transparent hover:border-border transition-colors
        "
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Meta grid
// ---------------------------------------------------------------------------

function MetaGrid({
  hotspot,
  rule,
  fileRange,
}: {
  readonly hotspot: Issue;
  readonly rule: Rule | undefined;
  readonly fileRange: string;
}) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
      <MetaRow label="Location">
        <span className="orb-code">{fileRange}</span>
      </MetaRow>
      <MetaRow label="Rule">
        <span className="orb-code">{hotspot.rule_id || '—'}</span>
      </MetaRow>
      {rule?.security_category ? (
        <MetaRow label="Category">
          <span>{rule.security_category}</span>
        </MetaRow>
      ) : null}
      {hotspot.detected_at ? (
        <MetaRow label="Detected">
          <span>{formatTimestamp(hotspot.detected_at)}</span>
        </MetaRow>
      ) : null}
      {hotspot.hotspot_reviewed_at ? (
        <MetaRow label="Last reviewed">
          <span>{formatTimestamp(hotspot.hotspot_reviewed_at)}</span>
        </MetaRow>
      ) : null}
    </dl>
  );
}

function MetaRow({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <>
      <dt className="text-fg-subtle">{label}</dt>
      <dd className="text-fg">{children}</dd>
    </>
  );
}

// ---------------------------------------------------------------------------
// Vulnerability context — CWE/OWASP tags + the rule's remediation hint.
// ---------------------------------------------------------------------------

function VulnerabilityContext({
  hotspot,
  rule,
  catalogLoading,
}: {
  readonly hotspot: Issue;
  readonly rule: Rule | undefined;
  readonly catalogLoading: boolean;
}) {
  const cwes = hotspot.cwe ?? [];
  const owasps = hotspot.owasp ?? [];
  const hasTags = cwes.length > 0 || owasps.length > 0;

  return (
    <section aria-label="Vulnerability context" className="space-y-3">
      <p className="orb-section-title">Vulnerability context</p>
      {hasTags ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {cwes.map((c) => (
            <span key={c} className="orb-badge border bg-bg-inset text-fg-muted border-border">
              CWE-{c}
            </span>
          ))}
          {owasps.map((o) => (
            <span key={o} className="orb-badge border bg-bg-inset text-fg-muted border-border">
              {o}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-fg-subtle">No CWE/OWASP classification on this finding.</p>
      )}
      {catalogLoading ? (
        <p className="text-xs text-fg-muted flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          Loading rule details…
        </p>
      ) : null}
      {!catalogLoading && rule?.why_it_matters ? (
        <p className="text-sm text-fg-muted leading-relaxed">{rule.why_it_matters}</p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Review panel — the actual T341 workflow: transition buttons + optional
// comment. Posts to POST /api/v1/hotspots/{issueID}/transition.
// ---------------------------------------------------------------------------

const TRANSITION_META: {
  status: HotspotReviewStatus;
  label: string;
  icon: ReactNode;
}[] = [
  { status: 'safe', label: 'Mark safe', icon: <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> },
  { status: 'fixed', label: 'Mark fixed', icon: <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> },
  { status: 'acknowledged', label: 'Acknowledge', icon: <Check className="h-3.5 w-3.5" aria-hidden="true" /> },
  { status: 'to_review', label: 'Reset to review', icon: <ShieldQuestion className="h-3.5 w-3.5" aria-hidden="true" /> },
];

function ReviewPanel({
  hotspot,
  status,
  onTransitioned,
}: {
  readonly hotspot: Issue;
  readonly status: HotspotReviewStatus;
  readonly onTransitioned: () => void;
}) {
  const [comment, setComment] = useState('');
  const [pending, setPending] = useState<HotspotReviewStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleTransition = async (next: HotspotReviewStatus) => {
    setPending(next);
    setError(null);
    setSuccess(null);
    try {
      await hotspotsApi.transition(hotspot.id, next, comment.trim() || undefined);
      setSuccess(`Marked ${STATUS_LABEL[next].toLowerCase()}.`);
      setComment('');
      onTransitioned();
    } catch (e: unknown) {
      setError(e instanceof ApiError ? `${e.code}: ${e.message}` : 'Transition failed.');
    } finally {
      setPending(null);
    }
  };

  return (
    <section aria-label="Review" className="space-y-3 border-t border-border pt-5">
      <p className="orb-section-title">Review</p>
      <div className="flex flex-wrap items-center gap-2">
        {TRANSITION_META.filter((t) => t.status !== status).map((t) => (
          <button
            key={t.status}
            type="button"
            onClick={() => handleTransition(t.status)}
            disabled={pending !== null}
            className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs
              font-semibold border border-border text-fg-muted hover:text-fg
              hover:border-fg-muted transition-colors
              disabled:opacity-40 disabled:cursor-not-allowed
            "
          >
            {pending === t.status ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              t.icon
            )}
            <span>{t.label}</span>
          </button>
        ))}
      </div>
      <div>
        <label htmlFor="hotspot-review-comment" className="sr-only">
          Optional review comment
        </label>
        <textarea
          id="hotspot-review-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Optional comment for this review (e.g. why it's safe)…"
          rows={2}
          className="
            w-full rounded-md border border-border bg-bg-inset
            px-3 py-2 text-sm text-fg placeholder:text-fg-subtle
            focus:outline-none focus:ring-1 focus:ring-accent/50
          "
        />
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      {success ? <p className="text-xs text-accent">{success}</p> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
